import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { IApiBackendRouteService } from '../abstract/api-routes.service';
import { API_ENDPOINTS } from '../../../core/providers/constants/api.constants';
import { WorkOrderType } from '../../../core/models/interface/workorder-type.interface';
import {
  ApiRouteWorkOrderTypes,
  WorkOrderAddBody,
} from '../dto/work-order.dto';
import { WorkOrder } from '../types/work-order';
import { WorkordersFromNetsuite } from '../types/workorder-from-netsuite';
import { changeDepartmentDto } from '../types/change-department-dto';
import { SchedulerWorkOrder } from '../../../common/scheduler/models/work-order';
import { SubWorkOrder } from '../types/sub-work-order';
import { BulkHosesDestination, BulkUploadResponse, HosesDestination, HosesDestinationCsvRow } from 'src/app/features/management/interfaces/hoses-destination.interface';

@Injectable({
  providedIn: 'root',
})
export class ApiRouteWorkOrderService extends IApiBackendRouteService {
  constructor(protected readonly http: HttpClient) {
    super();
  }

  urlRoute = `${API_ENDPOINTS.WORK_ORDERS}`;
  urlSchedulerRoute = `${API_ENDPOINTS.SCHEDULER}`;

  getAllWorkOrders(query: string = ''): Observable<any> {
    const apiURL = `${this.urlRoute}` + query;
    return this.http.get(apiURL);
  }

  getAllWorkOrderTypes(): Observable<WorkOrderType[]> {
    return this.http.get<WorkOrderType[]>(`${this.urlRoute}/type`);
  }

  getWorkOrder(transactionId: number): Observable<WorkOrder> {
    return this.http.get<WorkOrder>(`${this.urlRoute}/${transactionId}`);
  }

  findWorkOrder(workOrder: string): Observable<WorkOrder> {
    return this.http.get<WorkOrder>(
      `${this.urlSchedulerRoute}/${workOrder}/info`
    );
  }

  findAllByAssemblyItem(
    assemblyItemTitle: string,
    departmentName: string,
    limit: number
  ) {
    const params = new HttpParams({
      fromObject: {
        assemblyItemTitle,
        departmentName,
        limit,
      },
    });

    return this.http.get<WorkOrder[]>(`${this.urlRoute}/by-assembly`, {
      params,
    });
  }

  get(
    query: ApiRouteWorkOrderTypes['get']['query']
  ): Observable<ApiRouteWorkOrderTypes['get']['return']> {
    let params = new HttpParams();

    if (query.transaction) {
      params = params.set('transaction', query.transaction);
    }
    if (query.page) {
      params = params.set('page', query.page.toString());
    }
    if (query.limit) {
      params = params.set('limit', query.limit.toString());
    }
    const queryStr = params.keys().length > 0 ? `?${params.toString()}` : '';
    return this.http.get<ApiRouteWorkOrderTypes['get']['return']>(
      `${this.urlRoute}${queryStr}`
    );
  }

  getAvailableWorkOrders(
    typeId: number,
    query?: string
  ): Observable<SchedulerWorkOrder[]> {
    return this.http.get<SchedulerWorkOrder[]>(
      `${this.urlRoute}/available/${typeId}?${query}`
    );
  }

  getQuantityAvailableForChild(transactionId: number): Observable<number> {
    return this.http.get<number>(
      `${this.urlRoute}/${transactionId}/quantity-available`
    );
  }

  addWorkOrder(workOrder: WorkOrderAddBody): Observable<WorkOrder> {
    const apiURL = `${this.urlRoute}/?`;
    return this.http.post<WorkOrder>(apiURL, workOrder);
  }

  update(id: number, body: {}): Observable<WorkOrder> {
    return this.http.patch<WorkOrder>(`${this.urlRoute}/${id}`, body);
  }

  addWorkOrderFromNetsuiteBody(
    work: WorkordersFromNetsuite,
    id: number
  ): Observable<WorkOrder> {
    const apiurl = `${this.urlRoute}/fromnetsuite/${id}`;
    return this.http.post<WorkOrder>(apiurl, work);
  }

  changeDepartment(changeDeportmentDto: changeDepartmentDto): Observable<any> {
    const apiurl = `${this.urlRoute}/changedepartment`;
    return this.http.patch(apiurl, changeDeportmentDto);
  }

  getActiveWorkorderByAssembly(id: string): Observable<string[]> {
    return this.http.get<string[]>(this.urlRoute + '/assembly/' + id);
  }

  getSubWorkOrders(transactionId: number): Observable<SubWorkOrder[]> {
    return this.http.get<SubWorkOrder[]>(
      `${this.urlRoute}/${transactionId}/sub-work-orders`
    );
  }

  getHosesDestinationsTemplate(assemblyItemId: number): Observable<HosesDestination[]> {
    return this.http.get<HosesDestination[]>(
      `${this.urlRoute}/hoses-destinations-template/${assemblyItemId}`
    );
  }

  saveWorkOrderDestinations(workOrderId: number, payload: BulkHosesDestination): Observable<HosesDestination[]> {
    return this.http.post<HosesDestination[]>(
      `${this.urlRoute}/${workOrderId}/hoses-destinations`,
      payload
    );
  }

  bulkSaveHosesDestinations(payload: HosesDestinationCsvRow[]): Observable<BulkUploadResponse> {
    return this.http.post<BulkUploadResponse>(`${this.urlRoute}/hoses-destinations/bulk`, { destinations: payload });
  }

  getQuantities(
    transaction: string | number
  ): Observable<ApiRouteWorkOrderTypes['quantities']['result']> {
    return this.http.get<ApiRouteWorkOrderTypes['quantities']['result']>(
      `${this.urlRoute}/${transaction}/quantities`
    );
  }
}
